// Amazon Transcribe streaming's wire format: AWS event-stream messages over a
// WebSocket. Audio goes out as `AudioEvent` messages of 16-bit little-endian
// PCM; transcripts and exceptions come back as JSON bodies. No DOM and no
// Node APIs, so `e2e/dictation-codec.ts` runs it under Node as it is.

const encoder = new TextEncoder();
const decoder = new TextDecoder();

// Transcribe streaming's accepted PCM rates, inclusive
export const MIN_SAMPLE_RATE = 8000;
export const MAX_SAMPLE_RATE = 48000;

// CRC-32 (IEEE), which both checksums of an event-stream message use
const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

const crc32 = (bytes) => {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
};

const STRING_TYPE = 7;
// Value widths of the fixed-size header types, by type id; 6 (bytes) and 7
// (string) carry a 2-byte length of their own instead
const FIXED_SIZE = [0, 0, 1, 2, 4, 8, null, null, 8, 16];

// Prelude (total length, headers length, their CRC), headers, body, then a
// CRC of everything before it. Every header written here is a string.
export const encodeMessage = (headers, body) => {
  const fields = Object.entries(headers).map(([name, value]) => [
    encoder.encode(name),
    encoder.encode(value),
  ]);
  const headersLength = fields.reduce(
    (sum, [name, value]) => sum + 4 + name.length + value.length,
    0,
  );
  const total = 12 + headersLength + body.length + 4;
  const out = new Uint8Array(total);
  const view = new DataView(out.buffer);
  view.setUint32(0, total);
  view.setUint32(4, headersLength);
  view.setUint32(8, crc32(out.subarray(0, 8)));

  let at = 12;
  fields.forEach(([name, value]) => {
    out[at] = name.length;
    out.set(name, at + 1);
    at += 1 + name.length;
    out[at] = STRING_TYPE;
    view.setUint16(at + 1, value.length);
    out.set(value, at + 3);
    at += 3 + value.length;
  });
  out.set(body, at);
  at += body.length;
  view.setUint32(at, crc32(out.subarray(0, at)));
  return out;
};

// Headers other than strings are stepped over: Transcribe sends none
export const decodeMessage = (bytes) => {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const total = view.getUint32(0);
  const headersLength = view.getUint32(4);
  if (total !== bytes.length || 12 + headersLength + 4 > total) {
    throw new Error('Transcribe sent a malformed message');
  }
  if (
    view.getUint32(8) !== crc32(bytes.subarray(0, 8)) ||
    view.getUint32(total - 4) !== crc32(bytes.subarray(0, total - 4))
  ) {
    throw new Error('Transcribe sent a message with a bad checksum');
  }

  const headers = {};
  const headersEnd = 12 + headersLength;
  let at = 12;
  while (at < headersEnd) {
    const nameLength = bytes[at];
    const name = decoder.decode(bytes.subarray(at + 1, at + 1 + nameLength));
    at += 1 + nameLength;
    const type = bytes[at];
    at += 1;
    if (type >= FIXED_SIZE.length) {
      throw new Error('Transcribe sent an unknown header type');
    }
    const size = FIXED_SIZE[type] ?? view.getUint16(at);
    if (FIXED_SIZE[type] === null) at += 2;
    if (type === STRING_TYPE) {
      headers[name] = decoder.decode(bytes.subarray(at, at + size));
    }
    at += size;
  }
  return { headers, body: bytes.subarray(headersEnd, total - 4) };
};

// An empty one tells the service the audio has ended
export const audioFrame = (pcm) =>
  encodeMessage(
    {
      ':message-type': 'event',
      ':event-type': 'AudioEvent',
      ':content-type': 'application/octet-stream',
    },
    pcm,
  );

// {kind: 'transcript', results: [{id, partial, text}]} or
// {kind: 'error', type, message}
export const readFrame = (frame) => {
  const { headers, body: raw } = decodeMessage(frame);
  const body = decoder.decode(raw);
  const kind = headers[':message-type'];
  if (kind === 'exception' || kind === 'error') {
    let message = headers[':error-message'] ?? body;
    try {
      message = JSON.parse(body).Message ?? message;
    } catch {
      // Not JSON: the body is the message
    }
    return {
      kind: 'error',
      type: headers[':exception-type'] ?? headers[':error-code'] ?? 'Unknown',
      message,
    };
  }
  const parsed = JSON.parse(body || '{}');
  const results = (parsed.Transcript?.Results ?? []).map((result) => ({
    id: result.ResultId ?? '',
    partial: result.IsPartial === true,
    text: result.Alternatives?.[0]?.Transcript ?? '',
  }));
  return { kind: 'transcript', results };
};

// Float samples in [-1, 1] as 16-bit little-endian PCM
export const pcm16 = (samples) => {
  const out = new Uint8Array(samples.length * 2);
  const view = new DataView(out.buffer);
  for (let i = 0; i < samples.length; i += 1) {
    const sample = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(i * 2, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
  }
  return out;
};

// A partial result is revised in place under one id until a final one
// replaces it; finals arrive once each, in order
export class TranscriptAssembler {
  partials = new Map();

  // The final segments this message completed
  apply(results) {
    const done = [];
    results.forEach((result) => {
      if (result.partial) {
        this.partials.set(result.id, result.text);
        return;
      }
      this.partials.delete(result.id);
      const text = result.text.trim();
      if (text) done.push(text);
    });
    return done;
  }

  // What is being heard and may still change
  get partial() {
    return [...this.partials.values()].join(' ').trim();
  }
}

// A final stretch of speech after what is already in the box
export const appendSpoken = (current, spoken) => {
  if (!current.trim()) return spoken;
  return /\s$/.test(current) ? current + spoken : `${current} ${spoken}`;
};
